/* eslint-disable react/jsx-props-no-spreading */
import mergeClasses from '@srcDS/js/utils/shallowMerge';
import { useDevicesScreen } from '@srcDS/js/utils/useDevicesScreen';
import { ArrowLeft, ArrowRight } from '@srcDS/js/components/atoms/icons';
import CategoryTile from '@srcDS/js/components/molecules/categoryTile';
import { Heading } from '@srcDS/js/components/typography/heading/Heading';
import { Text } from '@srcDS/js/components/typography/text/Text';
import Title from '@srcDS/js/components/typography/title';
import defaultClasses from '@srcDS/js/components/organisms/categoriesSection/CategoriesSection.module.scss';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { StylesCustomizer } from '@srcDS/js/components/molecules/stylesCustomizer';
import { autoplayCarouselSlidesPerView } from '@srcDS/js/components/organisms/categoriesSection/_vars';

const CategoriesSection = ({
  categories,
  label,
  labelStyles,
  title,
  titleStyles,
  isArrowRight,
  withoutScroll,
  customClasses,
  customClassesForCategoryTile,
  tileLayout = 'stacked',
  placement,
  customBreakpoints = {},
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);

  const breakpoints = {
    320: {
      spaceBetween: 20,
      slidesPerView: 1.53,
      centeredSlides: true,
      initialSlide: 0,
    },
    680: {
      spaceBetween: 30,
      slidesPerView: 2.2,
      initialSlide: 1,
    },
    1120: {
      spaceBetween: 120,
      slidesPerView: 2.2,
      centeredSlides: true,
      initialSlide: 1,
    },
    ...customBreakpoints,
  };
  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useDevicesScreen();

  const checkFeatured = (items) => {
    return Array.from(items).find((item) => item.isFeatured);
  };

  const LabelText = (variables) => {
    return (
      <Text className={styles.cateogryLabel} as="span" weight="normal" textTransform="uppercase" variables={variables}>
        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </Text>
    );
  };
  const TitleText = (variables) => {
    return (
      <Heading
        align="center"
        className={styles.categoryHeader}
        as="h2"
        styleAs="h1"
        placement="plp"
        textTransform="uppercase"
        weight="light"
        variables={variables}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };
  const StyledLabelText = StylesCustomizer(LabelText);
  const StyledTitleText = StylesCustomizer(TitleText);

  const categoriesTitle =
    label || title ? (
      <Title className={styles.categoryTitle}>
        {label && <StyledLabelText styles={labelStyles} />}
        {title && <StyledTitleText styles={titleStyles} />}
      </Title>
    ) : null;

  const tiles = useMemo(() => {
    return categories ? (
      <div
        className={clsx(styles.inner, styles.staticGridInner, {
          [` ${styles.sectionWithFeatured}`]: checkFeatured(categories),
          [` ${styles.SC_searchDropdown}`]: placement === 'SearchDropdown',
        })}
      >
        {Array.from(categories).map((cat, index) => {
          const imageMd = cat.imageHeightMd || cat.imageHeightLg;
          const imageSm = cat.imageHeightsm ? cat.imageHeightsm : imageMd;
          return (
            <CategoryTile
              style={{
                '--image-height-lg': cat.imageHeightLg ? `${cat.imageHeightLg}%` : '125%',
                '--image-height-md': imageMd ? `${imageMd}%` : '125%',
                '--image-height-sm': imageSm ? `${imageSm}%` : '125%',
              }}
              key={`cat_${index}`}
              titleLink={cat.categoryUrl}
              align={cat.contentAlignment}
              title={cat.teaserText && cat.teaserText}
              description={cat.teaserDescription}
              titleStyles={cat.titleStyles}
              descriptionStyles={cat.descriptionStyles}
              image={
                cat.categoryImage && isMobile && cat.mobileCategoryImage ? cat.mobileCategoryImage : cat.categoryImage
              }
              links={cat.subcats && cat.subcats}
              isFeatured={cat.isFeatured}
              customClasses={{
                ...customClassesForCategoryTile,
                title: styles.title,
              }}
              linkObj={cat.categoryUrl}
              tileLayout={tileLayout}
            />
          );
        })}
      </div>
    ) : null;
  }, [categories]);

  if (!categories || !Array.from(categories).length) return null;

  return (
    <div className={styles.popularCategoriesSection}>
      {categoriesTitle}
      {isMobile && (
        <div
          className={clsx(styles.categoriesCarousel, {
            [` ${styles.carouselWithFeatured}`]: checkFeatured(categories),
          })}
        >
          <Swiper
            className="homeCategorySwiper"
            modules={[Navigation, Scrollbar]}
            breakpoints={breakpoints}
            slidesOffsetBefore={20}
            slidesOffsetAfter={20}
            navigation={{
              prevEl: '.swiper-prev-arrow',
              nextEl: '.swiper-next-arrow',
            }}
            scrollbar={{
              el: '.swiper-scrollbar',
              draggable: true,
              dragSize: 62,
            }}
            centeredSlidesBounds={true}
          >
            {categories
              ? Array.from(categories).map((cat, index) => {
                  const imageMd = cat.imageHeightMd || cat.imageHeightLg;
                  const imageSm = cat.imageHeightsm ? cat.imageHeightsm : imageMd;
                  return (
                    <SwiperSlide key={index} className={styles.wrapper}>
                      <CategoryTile
                        style={{
                          '--image-height-lg': cat.imageHeightLg ? `${cat.imageHeightLg}%` : '125%',
                          '--image-height-md': imageMd ? `${imageMd}%` : '125%',
                          '--image-height-sm': imageSm ? `${imageSm}%` : '125%',
                        }}
                        titleLink={cat.categoryUrl}
                        linkObj={cat.categoryUrl}
                        align={cat.contentAlignment}
                        title={cat.teaserText && cat.teaserText}
                        description={cat.teaserDescription}
                        titleStyles={cat.titleStyles}
                        descriptionStyles={cat.descriptionStyles}
                        image={
                          cat.categoryImage && isMobile && cat.mobileCategoryImage
                            ? cat.mobileCategoryImage
                            : cat.categoryImage
                        }
                        links={cat.subcats}
                        isFeatured={cat.isFeatured}
                        customClasses={{
                          ...customClassesForCategoryTile,
                          title: styles.title,
                        }}
                        tileLayout={tileLayout}
                      />
                    </SwiperSlide>
                  );
                })
              : null}
            {/* <div
              style={{
                display: isArrowRight ? 'block' : 'none',
              }}
            >
              <div
                data-carousel-arrow="arrow_left"
                className={`swiper-prev-arrow ${styles.arrowNavigation} ${styles.arrowNavigationLeft}`}
              >
                <ArrowLeft width="30px" height="30px" color="#000" />
              </div>
              <div
                data-carousel-arrow="arrow_right"
                className={`swiper-next-arrow ${styles.arrowNavigation} ${styles.arrowNavigationRight}`}
              >
                <ArrowRight width="30px" height="30px" color="#000" />
              </div>
            </div> */}
            {!withoutScroll && <div className={`swiper-scrollbar js-swiper-scrollbar ${styles.scrollbar}`} />}
          </Swiper>
        </div>
      )}
      {!isMobile && isTablet && (
        <Swiper
          freeMode={{ enabled: true, momentum: false }}
          edgeSwipeDetection="prevent"
          edgeSwipeThreshold={0}
          modules={[Navigation, Scrollbar]}
          spaceBetween={13}
          breakpoints={{
            320: {
              enabled: categories.length > autoplayCarouselSlidesPerView.mobile,
            },
            680: {
              enabled: categories.length > autoplayCarouselSlidesPerView.tablet,
            },
            980: {
              enabled: categories.length > autoplayCarouselSlidesPerView.desktop,
            },
          }}
        >
          <SwiperSlide className={styles.wrapper}>{tiles}</SwiperSlide>
        </Swiper>
      )}
      {(isDesktop || isLargeDesktop) && tiles}
    </div>
  );
};

export default CategoriesSection;
